import { PermissionSection } from '@shared/types/permissions'
import { normalize } from '@shared/utils'
import { useMemo, useState } from 'react'

import { PermissionSectionView, PermissionsStatusFilter } from './types'

const matches = (haystack: string | undefined, needle: string) =>
  !!haystack && normalize(haystack).includes(needle)

export const usePermissionsFilter = (
  sections: PermissionSection[],
  selected: Set<string>
) => {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState(PermissionsStatusFilter.ALL)

  const summaries = useMemo<PermissionSectionView[]>(
    () =>
      sections.map((section) => ({
        ...section,
        totalCount: section.options.length,
        activeCount: section.options.filter(({ key }) => selected.has(key))
          .length
      })),
    [sections, selected]
  )

  const visible = useMemo(() => {
    const needle = normalize(search)

    return summaries
      .map((section) => ({
        ...section,
        options: section.options.filter((option) => {
          const isActive = selected.has(option.key)

          if (status === PermissionsStatusFilter.ACTIVE && !isActive)
            return false
          if (status === PermissionsStatusFilter.INACTIVE && isActive)
            return false
          if (!needle) return true

          return (
            matches(option.label, needle) ||
            matches(option.value, needle) ||
            matches(option.description, needle)
          )
        })
      }))
      .filter(({ options }) => options.length > 0)
  }, [summaries, search, status, selected])

  return { search, setSearch, status, setStatus, summaries, visible }
}
